import { createCompNode } from '@wix/thunderbolt-catharsis'
import type { TransformData } from '@wix/thunderbolt-becky-types'
import type {
	BreakpointToHasVisibilityTransformation,
	ContainerVisibilityTransformationsRefs,
	ComponentVisibilityTransformationsRefs,
	VisibilityTransformationsDependencies,
} from './visibilityTransformations.types'
import { isRefArray } from '../../utils/variantsUtils'
import { toBreakpointVariantsMap } from '../../utils/toBreakpointVariantsMap'
import { DEFAULT_VARIANT_KEY } from '../transformations/transformationsDomApplier'

export const mergeVisibilityTransformations = (
	accumulateVisibilityTransformations?: BreakpointToHasVisibilityTransformation,
	componentVisibilityTransformations?: BreakpointToHasVisibilityTransformation
) => {
	return Object.entries(accumulateVisibilityTransformations || {}).reduce<BreakpointToHasVisibilityTransformation>(
		(acc, [breakpoint, hasVisibilityTransformation]) => ({
			...acc,
			[breakpoint]: acc[breakpoint] || hasVisibilityTransformation,
		}),
		componentVisibilityTransformations || {}
	)
}

export const containerVisibilityTransformationsFactory = () =>
	createCompNode<
		ContainerVisibilityTransformationsRefs,
		BreakpointToHasVisibilityTransformation,
		VisibilityTransformationsDependencies
	>({
		getDependencies: (component, refs) => {
			const containedComponents = component.components || []
			const componentsToTransformations = [component.id, ...containedComponents].map((componentId) => ({
				[componentId as string]: refs.componentVisibilityTransformations(componentId),
			}))
			return Object.assign({}, ...componentsToTransformations)
		},
		toViewItem: (component, data) => {
			const components = Object.values(Object.values(data))

			return components.reduce<BreakpointToHasVisibilityTransformation>(
				(acc, breakpointToHasVisibilityTransformation) =>
					mergeVisibilityTransformations(acc, breakpointToHasVisibilityTransformation),
				{}
			)
		},
	})

export const componentVisibilityTransformationsFactory = () =>
	createCompNode<
		ComponentVisibilityTransformationsRefs,
		BreakpointToHasVisibilityTransformation,
		'transformationQuery'
	>({
		getDependencies: (component, refs) =>
			component.transformationQuery ? refs.transformationQuery(component.transformationQuery) : undefined,
		toViewItem: (component, transformationRefArray) => {
			if (!isRefArray(transformationRefArray)) {
				return {}
			}

			const breakpointToTransformation = toBreakpointVariantsMap(transformationRefArray.values, (value) => value)

			const hasVisibilityTransformation = (
				defaultTransformation: TransformData,
				transformation: TransformData
			) => {
				const hiddenByDefault = !!defaultTransformation.hidden
				const visibleOnHover = !transformation.hidden

				const minOpacityByDefault = defaultTransformation.opacity === 0
				const maxOpacityOnHover = transformation.opacity === 1

				return (hiddenByDefault && visibleOnHover) || (minOpacityByDefault && maxOpacityOnHover)
			}

			return Object.entries(breakpointToTransformation).reduce(
				(acc, [breakpointId, transformationsInBreakpoint]) => {
					const defaultTransformationInBreakpoint =
						breakpointToTransformation?.[breakpointId][DEFAULT_VARIANT_KEY]

					const hasVisibilityTransformationInBreakpoint = Object.values(transformationsInBreakpoint).some(
						(transformation) =>
							defaultTransformationInBreakpoint &&
							transformation &&
							hasVisibilityTransformation(defaultTransformationInBreakpoint, transformation)
					)

					return { ...acc, [breakpointId]: hasVisibilityTransformationInBreakpoint }
				},
				{}
			)
		},
	})
